import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPenToSquare, faPlus, faRotateLeft, faTrash, faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Pagination,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  addClient,
  deleteClient,
  getClientDashboardStats,
  getClients,
  updateClient,
} from '../../service/apiService';

const emptyForm = {
  client_name: '',
  business_name: '',
  enable_portal: false,
  project_login_name: '',
  contact_person: '',
  email: '',
  phone: '',
  client_type: 'software_project',
  website: '',
  address: '',
  city: '',
  status: 'active',
  notes: '',
};

function ClientManagement({ user }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [filters, setFilters] = useState({ search: '', client_type: '', status: '', page: 1, limit: 10 });
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0 });

  const typeOptions = useMemo(() => [
    ['software_project', 'Software Project'],
    ['college_tieup', 'College Tie-up'],
    ['business_tieup', 'Business Tie-up'],
  ], []);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClients(filters);
      if (res.status === 'success') {
        setClients(res.data || []);
        setPagination(res.pagination || { page: 1, total_pages: 1, total: 0 });
      } else if (Array.isArray(res)) {
        setClients(res);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error('Client list error:', error);
      setMessage('Unable to load clients');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadStats = useCallback(async () => {
    try {
      const res = await getClientDashboardStats();
      if (res.status === 'success') setStats(res.data);
    } catch (error) {
      console.error('Client stats error:', error);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const statusVariant = (status) => {
    const colors = { active: 'success', prospect: 'info', on_hold: 'warning', inactive: 'secondary' };
    return colors[status] || 'secondary';
  };

  const projectCompletion = Number(stats?.total_projects)
    ? Math.round((Number(stats?.completed_projects || 0) / Number(stats.total_projects)) * 100)
    : 0;

  const handleFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const openAdd = () => {
    setEditingClient(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setFormData({ ...emptyForm, ...client, enable_portal: !!client.client_user_id });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.enable_portal && (!formData.project_login_name?.trim() || !formData.phone?.trim())) {
      setMessage('Project login name and phone number are required to enable client portal login');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const payload = { ...formData, admin_id: user?.id || 0 };
      const res = editingClient ? await updateClient({ id: editingClient.id, ...payload }) : await addClient(payload);
      if (res.status === 'success') {
        setShowModal(false);
        setMessage(editingClient ? 'Client updated successfully' : 'Client added successfully');
        await loadClients();
        await loadStats();
      } else {
        setMessage(res.message || 'Unable to save client');
      }
    } catch (error) {
      console.error('Save client error:', error);
      setMessage('Unable to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Delete ${client.client_name}? This will remove its projects and documents.`)) return;
    const res = await deleteClient(client.id);
    if (res.status === 'success') {
      setMessage('Client deleted successfully');
      loadClients();
      loadStats();
    } else {
      setMessage(res.message || 'Unable to delete client');
    }
  };

  return (
    <div className="client-management">
      <Breadcrumb>
        <Breadcrumb.Item>Staff</Breadcrumb.Item>
        <Breadcrumb.Item active>Client Management</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="mb-1">Client Management</h3>
          <div className="text-muted small">Manage client profiles, projects, assignments, and documents.</div>
        </div>
        <Button onClick={openAdd}><FontAwesomeIcon icon={faPlus} /> Add Client</Button>
      </div>

      {message && <Alert variant="info" onClose={() => setMessage('')} dismissible>{message}</Alert>}

      <Row className="g-3 mb-3">
        <Col md={3}><Card className="shadow-sm h-100"><Card.Body><div className="text-muted small">Total Clients</div><h4 className="mb-0">{stats?.total_clients || 0}</h4></Card.Body></Card></Col>
        <Col md={3}><Card className="shadow-sm h-100"><Card.Body><div className="text-muted small">Active Clients</div><h4 className="mb-0 text-success">{stats?.active_clients || 0}</h4></Card.Body></Card></Col>
        <Col md={3}><Card className="shadow-sm h-100"><Card.Body><div className="text-muted small">Ongoing Projects</div><h4 className="mb-0 text-primary">{stats?.ongoing_projects || 0}</h4></Card.Body></Card></Col>
        <Col md={3}>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between small text-muted"><span>Project Completion</span><span>{projectCompletion}%</span></div>
              <ProgressBar now={projectCompletion} className="mt-2" />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm p-3">
        <Row className="g-2 mb-3">
          <Col md={5}><Form.Control placeholder="Search client, email, phone, city..." value={filters.search} onChange={(e) => handleFilter('search', e.target.value)} /></Col>
          <Col md={3}>
            <Form.Select value={filters.client_type} onChange={(e) => handleFilter('client_type', e.target.value)}>
              <option value="">All Types</option>
              {typeOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Select value={filters.status} onChange={(e) => handleFilter('status', e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="prospect">Prospect</option>
              <option value="on_hold">On Hold</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Col>
          <Col md="auto" className="d-flex align-items-center">
  <FontAwesomeIcon
    icon={faRotateLeft}
    className="text-secondary"
    title="Reset Filters"
    style={{
      cursor: "pointer",
      fontSize: "20px",
    }}
    onClick={() =>
      setFilters({
        search: "",
        client_type: "",
        status: "",
        page: 1,
        limit: 10,
      })
    }
  />
</Col>
        </Row>

        {loading ? <div className="text-center py-4"><Spinner animation="border" /></div> : (
          <Table bordered hover responsive className="align-middle">
            <thead>
              <tr>
                <th>Code</th>
                <th>Client</th>
                <th>Type</th>
                <th>Contact</th>
                <th>Status</th>
                <th>City</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.length ? clients.map((client) => (
                <tr key={client.id}>
                  <td>{client.client_code || `CL${String(client.id).padStart(4, '0')}`}</td>
                  <td>
                    <button className="btn btn-link p-0 text-start" onClick={() => navigate(`/dashboard/clients/${client.id}`)}><strong>{client.client_name}</strong></button>
                    <div className="text-muted small">{client.business_name || client.contact_person || '-'}</div>
                  </td>
                  <td><Badge bg="info">{(client.client_type || '').replace('_', ' ')}</Badge></td>
                  <td><div>{client.phone || '-'}</div><div className="text-muted small">{client.email || '-'}</div></td>
                  <td><Badge bg={statusVariant(client.status)}>{client.status || 'active'}</Badge></td>
                  <td>{client.city || '-'}</td>
             <td>
  <div className="d-flex justify-content-center gap-3">

    <FontAwesomeIcon
      icon={faEye}
      className="text-primary"
      style={{ cursor: "pointer", fontSize: "18px" }}
      title="View"
      onClick={() => navigate(`/dashboard/clients/${client.id}`)}
    />

    <FontAwesomeIcon
      icon={faPenToSquare}
      className="text-warning"
      style={{ cursor: "pointer", fontSize: "18px" }}
      title="Edit"
      onClick={() => openEdit(client)}
    />

    <FontAwesomeIcon
      icon={faTrash}
      className="text-danger"
      style={{ cursor: "pointer", fontSize: "18px" }}
      title="Delete"
      onClick={() => handleDelete(client)}
    />

  </div>
</td>
                </tr>
              )) : <tr><td colSpan="7" className="text-center text-muted">No clients found</td></tr>}
            </tbody>
          </Table>
        )}

        {pagination.total_pages > 1 && (
          <Pagination className="justify-content-center mb-0">
            <Pagination.Prev disabled={filters.page <= 1} onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))} />
            {[...Array(pagination.total_pages)].map((_, index) => (
              <Pagination.Item key={index + 1} active={filters.page === index + 1} onClick={() => setFilters((prev) => ({ ...prev, page: index + 1 }))}>{index + 1}</Pagination.Item>
            ))}
            <Pagination.Next disabled={filters.page >= pagination.total_pages} onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))} />
          </Pagination>
        )}
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>{editingClient ? 'Edit Client' : 'Add Client'}</Modal.Title></Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}><Form.Label>Client Name</Form.Label><Form.Control name="client_name" value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} required /></Col>
              <Col md={6}><Form.Label>Business Name</Form.Label><Form.Control name="business_name" value={formData.business_name || ''} onChange={(e) => setFormData({ ...formData, business_name: e.target.value })} /></Col>
              <Col md={12}>
                <Card className="border bg-light">
                  <Card.Body>
                    <Form.Check
                      type="switch"
                      id="enable-client-portal"
                      label="Enable client portal login"
                      checked={!!formData.enable_portal}
                      onChange={(e) => setFormData({ ...formData, enable_portal: e.target.checked })}
                    />
                    <Row className="g-2 mt-2">
                      <Col md={7}>
                        <Form.Label>Project Login Name</Form.Label>
                        <Form.Control
                          value={formData.project_login_name || ''}
                          placeholder="Client will use this project name to login"
                          onChange={(e) => setFormData({ ...formData, project_login_name: e.target.value })}
                          disabled={!formData.enable_portal}
                        />
                      </Col>
                      <Col md={5}>
                        <Form.Label>Password Source</Form.Label>
                        <Form.Control value={formData.phone || ''} disabled placeholder="Client phone/mobile number" />
                      </Col>
                    </Row>
                    <div className="text-muted small mt-2">Client login uses this project name as username and the phone/mobile number as password.</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}><Form.Label>Contact Person</Form.Label><Form.Control name="contact_person" value={formData.contact_person || ''} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} /></Col>
              <Col md={3}><Form.Label>Email</Form.Label><Form.Control type="email" name="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></Col>
              <Col md={3}><Form.Label>Phone</Form.Label><Form.Control name="phone" value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></Col>
              <Col md={4}><Form.Label>Client Type</Form.Label><Form.Select name="client_type" value={formData.client_type} onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}>{typeOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Form.Select></Col>
              <Col md={4}><Form.Label>Status</Form.Label><Form.Select name="status" value={formData.status || 'active'} onChange={(e) => setFormData({ ...formData, status: e.target.value })}><option value="active">Active</option><option value="prospect">Prospect</option><option value="on_hold">On Hold</option><option value="inactive">Inactive</option></Form.Select></Col>
              <Col md={4}><Form.Label>Website</Form.Label><Form.Control name="website" value={formData.website || ''} onChange={(e) => setFormData({ ...formData, website: e.target.value })} /></Col>
              <Col md={8}><Form.Label>Address</Form.Label><Form.Control name="address" value={formData.address || ''} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></Col>
              <Col md={4}><Form.Label>City</Form.Label><Form.Control name="city" value={formData.city || ''} onChange={(e) => setFormData({ ...formData, city: e.target.value })} /></Col>
              <Col md={12}><Form.Label>Notes</Form.Label><Form.Control as="textarea" rows={3} name="notes" value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} /></Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}><FontAwesomeIcon icon={faFloppyDisk} /> {saving ? 'Saving...' : 'Save Client'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default ClientManagement;
